import { Download, Zap, Brain, ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const steps = [
  {
    icon: Download,
    title: "Install in 60 seconds",
    description: "One-click install from Microsoft AppSource. Works in Outlook Desktop, Web, and Mobile.",
    number: "1",
    color: "brand-pink",
    gradient: "from-brand-pink to-brand-orange",
  },
  {
    icon: Zap,
    title: "Turn emails into contacts",
    description: "Click 'Extract Contact' on any email — AI finds names, companies, and details. No manual typing.",
    number: "2",
    color: "brand-orange",
    gradient: "from-brand-orange to-brand-cyan",
  },
  {
    icon: Brain,
    title: "Get CRM intelligence",
    description: "Add Smart Categories and research notes. Start with 20 free credits, upgrade when you need more.",
    number: "3",
    color: "brand-cyan",
    gradient: "from-brand-cyan to-brand-blue",
  },
];

export const HowItWorks = () => {
  const [visibleSteps, setVisibleSteps] = useState<boolean[]>(new Array(steps.length).fill(false));
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observers = stepRefs.current.map((step, index) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setTimeout(() => {
                setVisibleSteps((prev) => {
                  const newVisible = [...prev];
                  newVisible[index] = true;
                  return newVisible;
                });
              }, index * 200);
            }
          });
        },
        { threshold: 0.2 }
      );
      
      if (step) observer.observe(step);
      return observer;
    });

    return () => {
      observers.forEach((observer, index) => {
        if (stepRefs.current[index]) {
          observer.unobserve(stepRefs.current[index]!);
        }
      });
    };
  }, []);

  return (
    <section id="how-it-works" className="py-32 bg-gradient-feature relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-20 w-72 h-72 bg-brand-pink/10 rounded-full blur-[100px] animate-blob" />
        <div className="absolute bottom-20 left-20 w-72 h-72 bg-brand-cyan/10 rounded-full blur-[100px] animate-blob animation-delay-2000" />
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] bg-[size:3rem_3rem] dark:bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)]" />

      <div className="container px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="inline-block px-4 py-2 mb-6 glass-card rounded-full">
            <span className="text-sm font-semibold bg-gradient-to-r from-brand-pink via-brand-orange to-brand-cyan bg-clip-text text-transparent">
              How It Works
            </span>
          </div>

          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Three steps.{" "}
            <span className="bg-gradient-to-r from-brand-pink via-brand-orange to-brand-cyan bg-clip-text text-transparent animate-gradient-shift">
              Zero complexity.
            </span>
          </h2>
          
          <p className="text-xl text-muted-foreground">
            Start with 20 free credits. Upgrade only when you need more.
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto">
          {steps.map((step, index) => (
            <div 
              key={index}
              ref={(el) => (stepRefs.current[index] = el)}
              className={`relative group transition-all duration-700 ${
                visibleSteps[index] 
                  ? 'opacity-100 translate-y-0 scale-100' 
                  : 'opacity-0 translate-y-12 scale-95'
              }`}
            >
              {/* Connecting Line */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-20 -right-6 w-12 h-0.5 bg-gradient-to-r from-primary/30 to-transparent z-0">
                  <ArrowRight className="absolute -right-1 -top-2 w-5 h-5 text-primary/30" />
                </div>
              )}

              {/* Card */}
              <div className="relative glass-card-strong rounded-2xl p-8 h-full border border-border/50 hover-lift hover-glow">
                {/* Number Badge */}
                <div className={`absolute -top-6 -left-6 w-16 h-16 bg-gradient-to-br ${step.gradient} rounded-2xl flex items-center justify-center shadow-elevated rotate-3 group-hover:rotate-6 transition-transform duration-300`}>
                  <span className="font-bold text-2xl text-white">{index + 1}</span>
                </div>
                
                {/* Icon with Animated Background */}
                <div className="mb-8 inline-flex p-5 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
                  <step.icon className="w-10 h-10 text-primary relative z-10 animate-float" />
                </div>
                
                <h3 className="text-2xl font-bold mb-4 group-hover:text-primary transition-colors">
                  {step.title}
                </h3>
                
                <p className="text-muted-foreground leading-relaxed text-lg">
                  {step.description}
                </p>

                {/* Decorative Corner Accent */}
                <div className={`absolute bottom-4 right-4 w-20 h-20 bg-gradient-to-br ${step.gradient} opacity-5 rounded-tl-[3rem] group-hover:opacity-10 transition-opacity`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
